<script setup lang="ts">
    import { computed, reactive } from 'vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import CopyButton from '@shared/components/CopyButton.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import Loading from '@shared/components/Loading.vue';
    import List from '@shared/components/List/List.vue';
    import Modal from '@shared/components/Modal.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import { getApiKeys, createApiKey, disableApiKey, ApiKeySummary } from './AccountApiKeys.access';

    const state = reactive<{
        loading: boolean;
        loadError: boolean;
        apiKeys: ApiKeySummary[];
        showAddDialog: boolean;
        newKeyName: string;
        generatedKey: string | null;
        creating: boolean;
        busyKeyIds: Set<string>;
        showingAllKeys: boolean;
    }>({
        loading: true,
        loadError: false,
        apiKeys: [],
        showAddDialog: false,
        newKeyName: '',
        generatedKey: null,
        creating: false,
        busyKeyIds: new Set(),
        showingAllKeys: false,
    });

    (async () => {
        try {
            state.apiKeys = await getApiKeys();
        } catch {
            state.loadError = true;
        } finally {
            state.loading = false;
        }
    })();

    const hasActiveApiKeys = computed(() => state.apiKeys.some((key) => !key.IsExpired));

    const tableHeaders = [$Resources.Name, $Resources.UserLabel, $Resources.ApiKeyExpiryLabel, ''];

    async function loadApiKeys(includeInactive = false): Promise<void> {
        state.loading = true;

        try {
            state.apiKeys = await getApiKeys(includeInactive);
            if (includeInactive) state.showingAllKeys = true;
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.errorHeading });
        } finally {
            state.loading = false;
        }
    }

    async function onCreateNewKey(): Promise<void> {
        state.creating = true;

        try {
            const result = await createApiKey(state.newKeyName);
            state.newKeyName = '';
            state.generatedKey = result.Key;
            state.showAddDialog = false;
            await loadApiKeys();
            bsAlert();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.errorHeading });
        } finally {
            state.creating = false;
        }
    }

    async function onDisableKey(key: ApiKeySummary): Promise<void> {
        state.busyKeyIds.add(key.ApiKeyId);

        try {
            await disableApiKey(key.ApiKeyId);
            key.IsExpired = true;
            bsAlert();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.errorHeading });
        } finally {
            state.busyKeyIds.delete(key.ApiKeyId);
        }
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.ManageApiKeys }}</h1>

    <Loading :show="state.loading" loadingStyle="progress-bar" fixed></Loading>

    <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

    <FeatureLock v-else feature="Authentication.APIKeys" :hideWhenLocked="false">
        <template v-if="!state.loading">
            <div class="alert-group">
                <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.ApiKeyAccountScopeInfo" />
                <BsAlert :type="AlertTypes.Info" v-if="!hasActiveApiKeys" :dismissible="false" :content="$Resources.NoActiveKeys" />
            </div>

            <BsAlert v-if="state.generatedKey && !state.showAddDialog" :type="AlertTypes.Success" :dismissible="false">
                <template #content>
                    <p>
                        {{ $Resources.GeneratedKeyHeader }}:
                        <strong>{{ state.generatedKey }}</strong>
                    </p>
                    <p>{{ $Resources.ApiKeySaveKeyInfoText }}</p>
                    <CopyButton :textToCopy="state.generatedKey" :buttonText="$Resources.CopyApiKeyTitle" class="btn-success"></CopyButton>
                </template>
            </BsAlert>

            <h2 class="h3">{{ $Resources.AddedKeysTitle }}</h2>

            <List :items="state.apiKeys" itemId="ApiKeyId" displayAs="table" :tableHeaders="tableHeaders" hideSearch>
                <template #toolbar>
                    <button type="button" class="btn btn-primary" @click="state.showAddDialog = true">{{ $Resources.AddApiKeyButton }}</button>
                    <button v-if="!state.showingAllKeys" type="button" class="btn btn-default" @click="loadApiKeys(true)">
                        {{ $Resources.ShowAllKeys }}
                    </button>
                </template>
                <template #item="{ item: key }">
                    <td :style="{ 'text-decoration': key.IsExpired ? 'line-through' : '' }">{{ key.Name }}</td>
                    <td>{{ key.UserName }} ({{ key.UserUserName }})</td>
                    <td :style="{ 'text-decoration': key.IsExpired ? 'line-through' : '' }">{{ formatDate(key.Expiry) }}</td>
                    <td class="text-right">
                        <ConfirmButton
                            v-if="!key.IsExpired"
                            buttonClasses="btn-sm"
                            :buttonText="$Resources.DisableKey"
                            :disabled="state.busyKeyIds.has(key.ApiKeyId)"
                            @confirm="onDisableKey(key)"
                            undoButton></ConfirmButton>
                    </td>
                </template>
            </List>

            <Modal size="sm" :show="state.showAddDialog" :title="$Resources.AddNewKey" @close="state.showAddDialog = false">
                <template #content>
                    <FormInput
                        class="form-group"
                        :label="$Resources.Name"
                        :modelValue="state.newKeyName"
                        :placeholder="$Resources.Name"
                        @update:modelValue="(newVal) => (state.newKeyName = String(newVal ?? ''))"></FormInput>
                </template>

                <template #button>
                    <button
                        class="btn btn-primary"
                        type="button"
                        :class="{ 'is-busy': state.creating }"
                        :disabled="!state.newKeyName || state.creating"
                        @click="onCreateNewKey">
                        {{ $Resources.AddNewApiKeyBtn }}
                    </button>
                </template>
            </Modal>
        </template>
    </FeatureLock>
</template>
